import React, { useState, useEffect } from 'react';
import { Star, Check, X, Trash2, RefreshCw, Clock, CheckCircle, XCircle, BadgeCheck, MessageSquareReply } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { toast } from 'sonner';
import axios from 'axios';
import { API } from '@/config';

const ReviewsManagement = ({ token, i18n }) => {
  const lang = i18n?.language || 'en';
  const [reviews, setReviews] = useState([]);
  const [filter, setFilter] = useState('all');
  const [counts, setCounts] = useState({});
  const [page, setPage] = useState(1);
  const [totalPages, setTotalPages] = useState(1);
  const [loading, setLoading] = useState(true);
  const [replyingId, setReplyingId] = useState(null);
  const [replyText, setReplyText] = useState('');
  const [savingReply, setSavingReply] = useState(false);

  const getText = (key) => {
    const t = {
      title: { ar: 'إدارة التقييمات', en: 'Reviews Management', sv: 'Hantera recensioner' },
      all: { ar: 'الكل', en: 'All', sv: 'Alla' },
      pending: { ar: 'في الانتظار', en: 'Pending', sv: 'Väntande' },
      approved: { ar: 'مقبول', en: 'Approved', sv: 'Godkänd' },
      rejected: { ar: 'مرفوض', en: 'Rejected', sv: 'Avvisad' },
      approve: { ar: 'قبول', en: 'Approve', sv: 'Godkänn' },
      reject: { ar: 'رفض', en: 'Reject', sv: 'Avvisa' },
      delete: { ar: 'حذف', en: 'Delete', sv: 'Ta bort' },
      noReviews: { ar: 'لا توجد تقييمات', en: 'No reviews', sv: 'Inga recensioner' },
      reply: { ar: 'رد', en: 'Reply', sv: 'Svara' },
      editReply: { ar: 'تعديل الرد', en: 'Edit reply', sv: 'Redigera svar' },
      saveReply: { ar: 'نشر الرد', en: 'Publish reply', sv: 'Publicera svar' },
      cancel: { ar: 'إلغاء', en: 'Cancel', sv: 'Avbryt' },
      deleteReply: { ar: 'حذف الرد', en: 'Delete reply', sv: 'Ta bort svar' },
      replyPlaceholder: { ar: 'اكتب رد DriveMaster... (يُترجم تلقائياً لكل اللغات)', en: 'Write DriveMaster reply... (auto-translated to all languages)', sv: 'Skriv DriveMasters svar... (översätts automatiskt)' },
      ownerReply: { ar: 'تم الرد من قبل DriveMaster', en: 'Reply from DriveMaster', sv: 'Svar från DriveMaster' },
      replySaved: { ar: 'تم نشر الرد', en: 'Reply published', sv: 'Svaret publicerat' },
      confirmDelete: { ar: 'هل أنت متأكد من الحذف؟', en: 'Are you sure you want to delete?', sv: 'Är du säker på att du vill ta bort?' },
    };
    return t[key]?.[lang] || t[key]?.en || key;
  };

  const fetchReviews = async () => {
    setLoading(true);
    try {
      const res = await axios.get(`${API}/admin/reviews?status=${filter}&page=${page}&limit=20`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setReviews(res.data.reviews || []);
      setCounts(res.data.counts || {});
      setTotalPages(res.data.pages || 1);
    } catch (err) {
      toast.error('Error fetching reviews');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { fetchReviews(); }, [filter, page]);

  const updateStatus = async (reviewId, status) => {
    try {
      await axios.put(`${API}/admin/reviews/${reviewId}`, { status }, {
        headers: { Authorization: `Bearer ${token}` }
      });
      toast.success(status === 'approved' ? getText('approved') : getText('rejected'));
      fetchReviews();
    } catch {
      toast.error('Error');
    }
  };

  const deleteReview = async (reviewId) => {
    if (!window.confirm(getText('confirmDelete'))) return;
    try {
      await axios.delete(`${API}/admin/reviews/${reviewId}`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      toast.success(getText('delete'));
      fetchReviews();
    } catch {
      toast.error('Error');
    }
  };

  const startReply = (review) => {
    setReplyingId(review.id);
    setReplyText(review.admin_reply?.original || '');
  };

  const saveReply = async (reviewId) => {
    if (replyText.trim().length < 2) return;
    setSavingReply(true);
    try {
      await axios.put(`${API}/admin/reviews/${reviewId}/reply`, { text: replyText.trim() }, {
        headers: { Authorization: `Bearer ${token}` }
      });
      toast.success(getText('replySaved'));
      setReplyingId(null);
      setReplyText('');
      fetchReviews();
    } catch {
      toast.error('Error');
    } finally {
      setSavingReply(false);
    }
  };

  const deleteReply = async (reviewId) => {
    if (!window.confirm(getText('confirmDelete'))) return;
    try {
      await axios.delete(`${API}/admin/reviews/${reviewId}/reply`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      fetchReviews();
    } catch {
      toast.error('Error');
    }
  };

  const showText = (v) => (typeof v === 'object' && v !== null ? (v[lang] || v.en || v.sv || Object.values(v)[0]) : v);

  const statusBadge = (status) => {
    const styles = {
      pending: 'bg-amber-100 text-amber-700',
      approved: 'bg-emerald-100 text-emerald-700',
      rejected: 'bg-red-100 text-red-700'
    };
    const icons = {
      pending: <Clock className="w-3 h-3" />,
      approved: <CheckCircle className="w-3 h-3" />,
      rejected: <XCircle className="w-3 h-3" />
    };
    return (
      <span className={`inline-flex items-center gap-1 px-2 py-1 rounded-full text-xs font-medium ${styles[status]}`}>
        {icons[status]}
        {getText(status)}
      </span>
    );
  };

  return (
    <div className="space-y-6" data-testid="reviews-management">
      <div className="flex flex-col sm:flex-row justify-between items-start sm:items-center gap-4">
        <h2 className="text-2xl font-bold text-slate-900 flex items-center gap-2">
          <Star className="w-6 h-6 text-amber-500" />
          {getText('title')}
        </h2>
        <Button variant="outline" size="sm" onClick={fetchReviews}>
          <RefreshCw className={`w-4 h-4 ${loading ? 'animate-spin' : ''}`} />
        </Button>
      </div>

      {/* Filter Tabs */}
      <div className="flex gap-2 flex-wrap">
        {['all', 'pending', 'approved', 'rejected'].map(f => (
          <button
            key={f}
            onClick={() => { setFilter(f); setPage(1); }}
            className={`px-4 py-2 rounded-full text-sm font-medium transition-all ${
              filter === f
                ? 'bg-slate-900 text-white'
                : 'bg-slate-100 text-slate-600 hover:bg-slate-200'
            }`}
            data-testid={`filter-${f}`}
          >
            {getText(f)} {counts[f] !== undefined ? `(${counts[f]})` : ''}
          </button>
        ))}
      </div>

      {/* Reviews List */}
      {loading ? (
        <div className="flex justify-center py-12">
          <RefreshCw className="w-8 h-8 animate-spin text-slate-400" />
        </div>
      ) : reviews.length === 0 ? (
        <div className="text-center py-12 text-slate-400">
          <Star className="w-12 h-12 mx-auto mb-3 opacity-30" />
          <p>{getText('noReviews')}</p>
        </div>
      ) : (
        <div className="space-y-3">
          {reviews.map(review => (
            <div key={review.id} className="bg-white rounded-xl p-5 border border-slate-200 shadow-sm" data-testid="admin-review-card">
              <div className="flex items-start justify-between flex-wrap gap-2 mb-3">
                <div className="flex items-center gap-3 min-w-0">
                  <div className="w-10 h-10 rounded-full bg-emerald-100 flex items-center justify-center font-bold text-emerald-700 text-sm">
                    {review.user_name?.charAt(0)?.toUpperCase() || '?'}
                  </div>
                  <div>
                    <p className="font-semibold text-slate-900 text-sm">{review.user_name}</p>
                    <p className="text-xs text-slate-400 break-all">{review.user_email}</p>
                  </div>
                </div>
                <div className="flex items-center gap-2">
                  <div className="flex gap-0.5">
                    {[1,2,3,4,5].map(s => (
                      <Star key={s} className={`w-4 h-4 ${s <= review.rating ? 'fill-amber-400 text-amber-400' : 'text-slate-200'}`} />
                    ))}
                  </div>
                  {statusBadge(review.status)}
                </div>
              </div>

              <p className="text-slate-600 text-sm mb-3 leading-relaxed">{showText(review.comment)}</p>

              {review.admin_reply && replyingId !== review.id && (
                <div className="mb-3 rounded-lg bg-sky-50 border border-sky-100 border-s-4 border-s-sky-500 p-3" data-testid="admin-review-reply">
                  <div className="flex items-center justify-between gap-2 mb-1">
                    <span className="flex items-center gap-1.5 text-xs font-bold text-sky-700">
                      <BadgeCheck className="w-4 h-4 text-white fill-sky-500" /> {getText('ownerReply')}
                    </span>
                    <button onClick={() => deleteReply(review.id)} className="text-xs text-red-500 hover:underline" data-testid="delete-reply-btn">
                      {getText('deleteReply')}
                    </button>
                  </div>
                  <p className="text-sm text-slate-700">{showText(review.admin_reply.text)}</p>
                </div>
              )}

              {replyingId === review.id && (
                <div className="mb-3 space-y-2" data-testid="admin-reply-editor">
                  <textarea
                    value={replyText}
                    onChange={(e) => setReplyText(e.target.value)}
                    placeholder={getText('replyPlaceholder')}
                    rows={3}
                    maxLength={1000}
                    className="w-full rounded-lg border border-sky-200 px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-sky-400 resize-none"
                    data-testid="admin-reply-input"
                  />
                  <div className="flex gap-2 justify-end">
                    <Button size="sm" variant="outline" onClick={() => setReplyingId(null)} data-testid="admin-reply-cancel-btn">
                      {getText('cancel')}
                    </Button>
                    <Button size="sm" onClick={() => saveReply(review.id)} disabled={savingReply || replyText.trim().length < 2}
                      className="bg-sky-500 hover:bg-sky-600 text-white" data-testid="admin-reply-save-btn">
                      {savingReply ? <RefreshCw className="w-4 h-4 me-1 animate-spin" /> : <BadgeCheck className="w-4 h-4 me-1" />}
                      {getText('saveReply')}
                    </Button>
                  </div>
                </div>
              )}

              <div className="flex items-center justify-between flex-wrap gap-2">
                <p className="text-xs text-slate-400">
                  {review.created_at ? new Date(review.created_at).toLocaleString() : ''}
                </p>
                <div className="flex gap-2 flex-wrap">
                  {replyingId !== review.id && (
                    <Button size="sm" variant="outline" onClick={() => startReply(review)}
                      className="text-sky-600 border-sky-200 hover:bg-sky-50" data-testid="reply-review-btn">
                      <MessageSquareReply className="w-4 h-4 me-1" /> {review.admin_reply ? getText('editReply') : getText('reply')}
                    </Button>
                  )}
                  {review.status !== 'approved' && (
                    <Button size="sm" variant="outline" onClick={() => updateStatus(review.id, 'approved')}
                      className="text-emerald-600 border-emerald-200 hover:bg-emerald-50" data-testid="approve-btn">
                      <Check className="w-4 h-4 me-1" /> {getText('approve')}
                    </Button>
                  )}
                  {review.status !== 'rejected' && (
                    <Button size="sm" variant="outline" onClick={() => updateStatus(review.id, 'rejected')}
                      className="text-red-600 border-red-200 hover:bg-red-50" data-testid="reject-btn">
                      <X className="w-4 h-4 me-1" /> {getText('reject')}
                    </Button>
                  )}
                  <Button size="sm" variant="outline" onClick={() => deleteReview(review.id)}
                    className="text-slate-500 hover:text-red-600 hover:bg-red-50" data-testid="delete-review-btn">
                    <Trash2 className="w-4 h-4" />
                  </Button>
                </div>
              </div>
            </div>
          ))}
        </div>
      )}

      {/* Pagination */}
      {totalPages > 1 && (
        <div className="flex justify-center gap-2">
          <Button variant="outline" size="sm" onClick={() => setPage(p => Math.max(1, p-1))} disabled={page <= 1}>
            ←
          </Button>
          <span className="px-3 py-2 text-sm text-slate-500">{page} / {totalPages}</span>
          <Button variant="outline" size="sm" onClick={() => setPage(p => Math.min(totalPages, p+1))} disabled={page >= totalPages}>
            →
          </Button>
        </div>
      )}
    </div>
  );
};

export default ReviewsManagement;
